import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api, { FilterPreferences } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import TableColumnFilter from '../../components/TableColumnFilter';
import FilterDropdown, { FilterSection } from '../../components/FilterDropdown';
import ActiveFilterChips, { FilterChip } from '../../components/ActiveFilterChips';
import { useListLayoutPreferences } from '../../hooks/useListLayoutPreferences';
import { useFilterPreferences } from '../../hooks/useFilterPreferences';
import ListLayoutSettingsModal from '../../components/ListLayoutSettingsModal';
import ListLayoutSettingsButton from '../../components/ListLayoutSettingsButton';
import ApprovalStatusBadge from '../../components/ApprovalStatusBadge';
import TimeEntryApprovalModal from '../../components/TimeEntryApprovalModal';
import TimeEntryRejectionModal from '../../components/TimeEntryRejectionModal';
import TimeEntryActivityModal from '../../components/TimeEntryActivityModal';
import CopyTimeEntryModal from '../../components/CopyTimeEntryModal';
import TimeEntryImportModal from '../../components/TimeEntryImportModal';
import HierarchicalTimeEntryTable from '../../components/HierarchicalTimeEntryTable';
import TimeEntryInlineEditableCell from '../../components/TimeEntryInlineEditableCell';
import { usePersistListFiltersSession } from '../../hooks/usePersistListFiltersSession';
import { LIST_FILTER_SESSION_KEYS } from '../../constants/listFilterSessionKeys';
import { stripHtml } from '../../utils/sanitize';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  CheckCircleIcon,
  XCircleIcon,
  XMarkIcon,
  ClockIcon,
  ClipboardDocumentListIcon,
  PencilIcon,
  DocumentDuplicateIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

interface TimeEntry {
  id: string;
  number?: string;
  userId: string;
  customerId: string;
  engagementId: string;
  groupId: string;
  type: string;
  timeSpent: number;
  workedDate: string;
  description: string;
  task?: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  remarks?: string;
  actionRequired?: string;
  approvedAt?: string;
  rejectedAt?: string;
  createdAt: string;
  Customer?: { id: string; accountName: string };
  Engagement?: { id: string; name: string; number: string };
  Group?: { id: string; name: string };
  User?: { id: string; firstName: string; lastName: string; email: string };
  ApprovedBy?: { id: string; firstName: string; lastName: string };
  RejectedBy?: { id: string; firstName: string; lastName: string };
}

interface TimeEntryActivity {
  id: string;
  action: 'SUBMITTED' | 'APPROVED' | 'REJECTED' | 'RESUBMITTED';
  previousStatus: string | null;
  newStatus: string | null;
  remarks: string | null;
  actionRequired: string | null;
  createdAt: string;
  Actor: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface UserOption {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

const TYPE_LABELS: Record<string, string> = {
  ACCESS_DE_PROVISION: 'Access De-Provision',
  ACCESS_PROVISION: 'Access Provision',
  BULK_CLOSURE_ADMIN_CASES: 'Bulk closure of Admin Cases',
  CAB_MEETING: 'CAB Meeting',
  CROWDSTRIKE_DASHBOARD_MONITORING: 'CrowdStrike Dashboard Monitoring',
  CUSTOMER_MEETINGS: 'Customer Meetings',
  EXTERNAL_AUDIT: 'External Audit',
  THIRD_PARTY_VENDOR_CALL: '3rd Party Vendor Call',
  HANDOVER: 'Handover',
  INTERNAL_AUDIT: 'Internal Audit',
  INTERNAL_MEETINGS: 'Internal Meetings',
  INTERVIEW: 'Interview',
  MONTHLY_MSR_EXTERNAL_REVIEW: 'Monthly MSR External Review',
  MONTHLY_MSR_INTERNAL_REVIEW: 'Monthly MSR Internal Review',
  MONTHLY_MSR_REPORT_PREPARATION: 'Monthly MSR Report preparation',
  PEP_DISCUSSION: 'PEP Discussion',
  PROOF_OF_CONCEPT: 'Proof of Concept',
  REPORT_PREPARATION_OTHER: 'Report preparation (Other)',
  REPORTING: 'Reporting',
  ROASTER_PREPARATION: 'Roaster Preparation',
  TRAINING_NEW_RESOURCE: 'Training - New Resource',
  TRAININGS: 'Trainings',
  WEEKLY_LEADERSHIP_CONNECT: 'Weekly Leadership Connect',
  WEEKLY_TEAM_CONNECT: 'Weekly Team Connect',
  AV_DASHBOARD_REVIEW: 'AV Dashboard review',
  US_HEALTH_CHECK: 'US Health Check',
  UK_HEALTH_CHECK: 'UK Health Check',
  DAILY_VM_BACKUP_REPORT_CHECK: 'Daily VM Backup Report Check',
  DAILY_DB_BACKUP_REPORT_CHECK: 'Daily DB Backup Report Check',
  ALWAYS_ON_REPORT_CHECK: 'Always On Report Check',
  DAILY_SNOW_SCRIPT_TASK: 'Daily SNOW Script Task',
  DAILY_VERICHECK_ACH_MONITORING: 'Daily VeriCheck ACH Monitoring',
  MONTHLY_TICKET_CREATION: 'Monthly Ticket Creation',
  MONTHLY_ODM_REPORT_CREATION: 'Monthly ODM Report Creation',
  REINDEXING_REPORT_MONITORING: 'Reindexing Report Monitoring',
  PIP_DISCUSSION: 'PIP discussion',
  INTERNAL_EXTERNAL_ESCALATION_DISCUSSION: 'Internal/External Escalation Discussion',
  CHANGE_SERVER_CONFIGURATION: 'Change Server Configuration',
  INITIAL_INVESTIGATION_TROUBLESHOOTING: 'Initial Investigation and Troubleshooting',
  CONFIGURATION: 'Configuration',
  HEALTH_CHECK: 'Health Check',
  OTHER_PREVENTIVE_MAINTENANCE: 'Other Preventive Maintenance',
  SERVER_PROVISIONING: 'Server Provisioning',
  TUNING_OPTIMIZATION: 'Tuning Optimization',
  UPDATES_PATCHES: 'Updates/Patches',
  ACCOUNT_PLANS: 'Account Plans',
  TRANSFORMATION_INITIATIVES: 'Transformation Initiatives',
  MONITORING_ENHANCEMENTS: 'Monitoring Enhancements',
  SELF_HEALING_FRAMEWORKS: 'Self-Healing Frameworks',
  AI_GENAI_USE_CASES: 'AI/GenAI Use Cases',
  PROJECT_MEETING: 'Project Meeting',
};

const DEPRECATED_TYPE_LABELS: Record<string, string> = {
  OTHER_ADMINISTRATIVE_WORK: 'Administrative work',
  OTHER_GENERAL_MEETINGS: 'General Meetings & Discussion',
};

const getTypeLabel = (type: string) => TYPE_LABELS[type] || DEPRECATED_TYPE_LABELS[type] || type;

// Status options
const statusOptions = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
];

// Time entered preset options (values are in minutes)
const timeEnteredPresetOptions = [
  { value: 'lt30', label: 'Less than 30 mins', minMinutes: null, maxMinutes: 29 },
  { value: 'lt60', label: 'Less than 1 hour', minMinutes: null, maxMinutes: 59 },
  { value: 'gt60', label: 'Above 1 hour', minMinutes: 61, maxMinutes: null },
  { value: 'gt120', label: 'Above 2 hours', minMinutes: 121, maxMinutes: null },
  { value: 'custom', label: 'Custom Range', minMinutes: null, maxMinutes: null },
];

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200] as const;

const TIME_ENTRY_LIST_URL_FILTER_KEYS = [
  'search',
  'number',
  'desc',
  'task',
  'month',
  'customer',
  'engagement',
  'type',
  'user',
  'group',
  'status',
  'time',
  'hierarchy',
] as const;

function parseTimePresetFromUrl(timeParam: string | null): {
  preset: string;
  min: number | null;
  max: number | null;
} {
  if (!timeParam) return { preset: '', min: null, max: null };
  if (timeParam.startsWith('custom:')) {
    const [, range] = timeParam.split('custom:');
    const [min, max] = (range || '').split(',');
    return {
      preset: timeParam,
      min: min ? parseInt(min, 10) : null,
      max: max ? parseInt(max, 10) : null,
    };
  }
  const preset = timeEnteredPresetOptions.find((p) => p.value === timeParam);
  return {
    preset: timeParam,
    min: preset?.minMinutes ?? null,
    max: preset?.maxMinutes ?? null,
  };
}

function isDefaultTimeEntryListUrl(sp: URLSearchParams): boolean {
  const page = sp.get('page');
  if (page && page !== '1') return false;
  const ps = sp.get('pageSize');
  if (ps && ps !== '25') return false;
  for (const k of TIME_ENTRY_LIST_URL_FILTER_KEYS) {
    const v = sp.get(k);
    if (v !== null && v !== '') return false;
  }
  return true;
}

function buildTimeEntryListUrlUpdatesFromSaved(
  savedFilters: FilterPreferences
): Record<string, string | null | undefined> {
  const updates: Record<string, string | null | undefined> = {};
  if (savedFilters.type && Array.isArray(savedFilters.type) && savedFilters.type.length) {
    updates.type = savedFilters.type.join(',');
  }
  if (savedFilters.status && Array.isArray(savedFilters.status) && savedFilters.status.length) {
    updates.status = savedFilters.status.join(',');
  }
  if (savedFilters.month) {
    const m =
      typeof savedFilters.month === 'string'
        ? savedFilters.month.split(',').filter(Boolean)
        : (savedFilters.month as string[]);
    if (m.length) updates.month = m.join(',');
  }
  if (savedFilters.customer) updates.customer = savedFilters.customer;
  if (savedFilters.engagement) {
    const e =
      typeof savedFilters.engagement === 'string'
        ? savedFilters.engagement.split(',').filter(Boolean)
        : (savedFilters.engagement as string[]);
    if (e.length) updates.engagement = e.join(',');
  }
  if (savedFilters.user) {
    const u =
      typeof savedFilters.user === 'string'
        ? savedFilters.user.split(',').filter(Boolean)
        : (savedFilters.user as string[]);
    if (u.length) updates.user = u.join(',');
  }
  return updates;
}

// Get current month value in YYYY-MM format
const getCurrentMonth = () => {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
};

// Generate month options for the last 12 months
const getMonthOptions = () => {
  const options = [];
  const today = new Date();
  for (let i = 0; i < 12; i++) {
    const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const label = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    options.push({ value, label });
  }
  return options;
};

// Interface for export data
interface TimeEntryExport {
  workedDate: string;
  userName: string;
  userEmail: string;
  newEmployeeId: string;
  employeeEntity: string;
  customerName: string;
  engagementName: string;
  engagementNumber: string;
  groupName: string;
  type: string;
  typeLabel: string;
  task: string;
  timeSpentMinutes: number;
  timeSpentHours: number;
  hourlyRate: number;
  totalAmount?: number;
  description: string;
}

// Interface for export with activity data
interface TimeEntryActivityExport {
  entryNumber: string;
  dateOfActivity: string;
  activityType: string;
  task: string;
  userName: string;
  newEmployeeId: string;
  employeeEntity: string;
  customerName: string;
  engagementName: string;
  groupName: string;
  timeSpentMinutes: number;
  comments: string;
  rmName: string;
  isEffortCorrect: string;
  effortsApproval: string;
  auditTrail: string; // Full audit trail with all activities
  actionRemarkUpdatedOn: string;
}

// Interface for user's own entries export
interface MyTimeEntryExport {
  entryNumber: string;
  workedDate: string;
  newEmployeeId: string;
  employeeEntity: string;
  customerName: string;
  engagementName: string;
  engagementNumber: string;
  groupName: string;
  type: string;
  typeLabel: string;
  task: string;
  timeSpentMinutes: number;
  timeSpentHours: number;
  description: string;
  status: string;
}

// Generate CSV content from time entry data
const generateTimeEntryCSV = (entries: TimeEntryExport[]): string => {
  const headers = [
    'Worked Date',
    'User Name',
    'User Email',
    'Employee ID',
    'Employee Entity',
    'Customer',
    'Engagement',
    'Engagement Number',
    'Group',
    'Activity Type',
    'Task',
    'Time (Hours)',
    'Time (Minutes)',
    'Rate ($/hr)',
    'Total Amount',
    'Description',
  ];
  const csvRows = [headers.join(',')];

  let grandTotal = 0;

  entries.forEach(entry => {
    const totalAmount = entry.totalAmount ?? (entry.timeSpentHours * entry.hourlyRate);
    grandTotal += totalAmount;

    const escapedRow = [
      entry.workedDate,
      `"${entry.userName.replace(/"/g, '""')}"`,
      entry.userEmail,
      entry.newEmployeeId,
      `"${(entry.employeeEntity || '').replace(/"/g, '""')}"`,
      `"${entry.customerName.replace(/"/g, '""')}"`,
      `"${entry.engagementName.replace(/"/g, '""')}"`,
      entry.engagementNumber,
      `"${entry.groupName.replace(/"/g, '""')}"`,
      `"${entry.typeLabel.replace(/"/g, '""')}"`,
      `"${(entry.task || '').replace(/"/g, '""')}"`,
      entry.timeSpentHours.toFixed(2),
      entry.timeSpentMinutes.toString(),
      entry.hourlyRate.toFixed(2),
      totalAmount.toFixed(2),
      `"${(entry.description || '').replace(/"/g, '""')}"`,
    ];
    csvRows.push(escapedRow.join(','));
  });

  // Add empty row before total
  csvRows.push('');

  // Add total row - empty cells for all columns except "Total Amount"
  const totalRow = [
    '', '', '', '', '', '', '', '', '', '', '', '', '', 'Total',
    grandTotal.toFixed(2),
    ''
  ];
  csvRows.push(totalRow.join(','));

  return csvRows.join('\n');
};

// Download CSV file
const downloadCSV = (content: string, filename: string) => {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Generate CSV content with activity details (for audit export)
const generateTimeEntryActivityCSV = (entries: TimeEntryActivityExport[]): string => {
  const headers = [
    'Entry #',
    'Date Of Activity',
    'Activity Type',
    'Task',
    'User',
    'Employee ID',
    'Employee Entity',
    'Customer',
    'Engagement',
    'Group',
    'Time Spent In Min.',
    'Comments',
    'RM Name',
    'Is the effort Correct?',
    'Efforts Approval',
    'Audit Trail',
    'Last Updated On'
  ];
  const csvRows = [headers.join(',')];

  entries.forEach(entry => {
    const escapedRow = [
      entry.entryNumber,
      entry.dateOfActivity,
      `"${(entry.activityType || '').replace(/"/g, '""')}"`,
      `"${(entry.task || '').replace(/"/g, '""')}"`,
      `"${(entry.userName || '').replace(/"/g, '""')}"`,
      entry.newEmployeeId,
      `"${(entry.employeeEntity || '').replace(/"/g, '""')}"`,
      `"${(entry.customerName || '').replace(/"/g, '""')}"`,
      `"${(entry.engagementName || '').replace(/"/g, '""')}"`,
      `"${(entry.groupName || '').replace(/"/g, '""')}"`,
      entry.timeSpentMinutes.toString(),
      `"${(entry.comments || '').replace(/"/g, '""').replace(/\n/g, ' ')}"`,
      `"${(entry.rmName || '').replace(/"/g, '""')}"`,
      entry.isEffortCorrect,
      entry.effortsApproval,
      `"${(entry.auditTrail || '').replace(/"/g, '""')}"`, // Preserve newlines for multi-line display in Excel cell
      entry.actionRemarkUpdatedOn
    ];
    csvRows.push(escapedRow.join(','));
  });

  return csvRows.join('\n');
};

// Generate CSV content for user's own time entries
const generateMyTimeEntryCSV = (entries: MyTimeEntryExport[]): string => {
  const headers = [
    'Entry #',
    'Worked Date',
    'Employee ID',
    'Employee Entity',
    'Customer',
    'Engagement',
    'Engagement Number',
    'Group',
    'Activity Type',
    'Task',
    'Time (Hours)',
    'Time (Minutes)',
    'Status',
    'Description'
  ];
  const csvRows = [headers.join(',')];

  let totalMinutes = 0;

  entries.forEach(entry => {
    totalMinutes += entry.timeSpentMinutes;
    const escapedRow = [
      entry.entryNumber,
      entry.workedDate,
      entry.newEmployeeId,
      `"${(entry.employeeEntity || '').replace(/"/g, '""')}"`,
      `"${(entry.customerName || '').replace(/"/g, '""')}"`,
      `"${(entry.engagementName || '').replace(/"/g, '""')}"`,
      entry.engagementNumber,
      `"${(entry.groupName || '').replace(/"/g, '""')}"`,
      `"${(entry.typeLabel || '').replace(/"/g, '""')}"`,
      `"${(entry.task || '').replace(/"/g, '""')}"`,
      entry.timeSpentHours.toFixed(2),
      entry.timeSpentMinutes.toString(),
      entry.status,
      `"${(entry.description || '').replace(/"/g, '""').replace(/\n/g, ' ')}"`,
    ];
    csvRows.push(escapedRow.join(','));
  });

  // Add empty row before total
  csvRows.push('');

  // Add total row
  const totalHours = (totalMinutes / 60).toFixed(2);
  const totalRow = [
    '', '', '', '', '', '', '', '', '', 'Total',
    totalHours,
    totalMinutes.toString(),
    '', ''
  ];
  csvRows.push(totalRow.join(','));

  return csvRows.join('\n');
};

export default function TimeEntryList() {
  const { user, hasAnyRole } = useAuth();
  const {
    canViewEntity,
    canCreateEntity,
    canUpdateEntity,
    canDeleteEntity,
    loading: permissionsLoading,
  } = useUserPermissions();
  const queryClient = useQueryClient();
  const { formatDate: formatDateHook, formatDateOnly, getToday } = useDateFormatter();

  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  usePersistListFiltersSession('/time-entries', LIST_FILTER_SESSION_KEYS.timeEntries);

  const patchSearchParams = useCallback(
    (updates: Record<string, string | null | undefined>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, val] of Object.entries(updates)) {
            if (val === null || val === undefined || val === '') {
              next.delete(key);
            } else {
              next.set(key, val);
            }
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const canAccessTimeEntries = canViewEntity('time_entries');
  const canCreateTimeEntries = canCreateEntity('time_entries');
  const canEditTimeEntries = canUpdateEntity('time_entries');
  const isAdmin = hasAnyRole(['admin', 'super_admin']);
  const isAdminOrManager = hasAnyRole(['admin', 'manager', 'super_admin']);
  const canDeleteTimeEntries = canDeleteEntity('time_entries');
  const canExportAll = hasAnyRole(['admin', 'super_admin', 'finance', 'manager']); // Full export with customer/engagement filters
  const canExportMyEntries = canAccessTimeEntries; // All users can export their own entries

  const canModifyTimeEntry = useCallback(
    (entry: TimeEntry) =>
      canEditTimeEntries && (isAdmin || entry.userId === user?.id),
    [canEditTimeEntries, isAdmin, user?.id]
  );

  const canCopyTimeEntry = useCallback(
    (entry: TimeEntry) =>
      canCreateTimeEntries && (isAdmin || entry.userId === user?.id),
    [canCreateTimeEntries, isAdmin, user?.id]
  );

  const canDeleteTimeEntry = useCallback(
    (entry: TimeEntry) =>
      canDeleteTimeEntries && (isAdmin || entry.userId === user?.id),
    [canDeleteTimeEntries, isAdmin, user?.id]
  );

  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') ?? '');
  const appliedSearchTerm = searchParams.get('search') ?? '';
  useEffect(() => {
    setSearchInput(appliedSearchTerm);
  }, [appliedSearchTerm]);

  const page = useMemo(() => {
    const p = parseInt(searchParams.get('page') || '1', 10);
    return Number.isFinite(p) && p >= 1 ? p : 1;
  }, [searchParams]);

  const pageSize = useMemo(() => {
    const ps = parseInt(searchParams.get('pageSize') || '25', 10);
    return PAGE_SIZE_OPTIONS.includes(ps as (typeof PAGE_SIZE_OPTIONS)[number]) ? ps : 25;
  }, [searchParams]);

  const filterMonth = useMemo(() => {
    const m = searchParams.get('month');
    if (m === 'all') return [];
    if (m) return m.split(',').filter(Boolean);
    // Default landing URL: prefs may still hydrate. Any other explicit filters without month: use current month (prior behavior).
    return [getCurrentMonth()];
  }, [searchParams]);

  const filterCustomer = searchParams.get('customer') ?? '';

  const filterEngagement = useMemo(
    () => (searchParams.get('engagement') || '').split(',').filter(Boolean),
    [searchParams]
  );

  const filterType = useMemo(
    () => (searchParams.get('type') || '').split(',').filter(Boolean),
    [searchParams]
  );

  const filterUser = useMemo(
    () => (searchParams.get('user') || '').split(',').filter(Boolean),
    [searchParams]
  );

  const filterGroup = useMemo(
    () => (searchParams.get('group') || '').split(',').filter(Boolean),
    [searchParams]
  );

  const filterStatus = useMemo(
    () => (searchParams.get('status') || '').split(',').filter(Boolean),
    [searchParams]
  );

  const filterNumber = searchParams.get('number') ?? '';
  const filterDesc = searchParams.get('desc') ?? '';
  const filterTask = searchParams.get('task') ?? '';

  const filterTimeRangeParsed = useMemo(
    () => parseTimePresetFromUrl(searchParams.get('time')),
    [searchParams]
  );
  const filterTimePreset = filterTimeRangeParsed.preset;
  const filterTimeMin = filterTimeRangeParsed.min;
  const filterTimeMax = filterTimeRangeParsed.max;

  const showMyHierarchy = useMemo(() => {
    if (isAdminOrManager) return searchParams.get('hierarchy') === '1';
    return false;
  }, [isAdminOrManager, searchParams]);

  const [filtersInitialized, setFiltersInitialized] = useState(false);
  const savedPrefsHydratedRef = useRef(false);

  const monthOptions = getMonthOptions();

  // Export modal state
  const [showExportModal, setShowExportModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [exportCustomerId, setExportCustomerId] = useState('');
  const [exportEngagementId, setExportEngagementId] = useState('');
  const [exportMonth, setExportMonth] = useState(monthOptions[0]?.value || '');
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const [includeActivityDetails, setIncludeActivityDetails] = useState(false); // Include approval audit details

  // Modal states
  const [showLayoutSettings, setShowLayoutSettings] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<TimeEntry | null>(null);
  const [entryToCopy, setEntryToCopy] = useState<TimeEntry | null>(null);
  const [activities, setActivities] = useState<TimeEntryActivity[]>([]);
  const [isLoadingActivities, setIsLoadingActivities] = useState(false);

  // Bulk action states
  const [showBulkApprovalModal, setShowBulkApprovalModal] = useState(false);
  const [showBulkRejectionModal, setShowBulkRejectionModal] = useState(false);
  const [selectedEntriesForBulk, setSelectedEntriesForBulk] = useState<TimeEntry[]>([]);
  const [entryToDelete, setEntryToDelete] = useState<TimeEntry | null>(null);

  // Toast notification state
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' } | null>(null);

  const todayDate = getToday();

  // Auto-dismiss toast
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // Filter preferences
  const {
    filters: savedFilters,
    saveFilters,
    isSaving: isFilterSaving,
    isFetched: isFilterPrefsFetched,
  } = useFilterPreferences('TIME_ENTRY');

  // Apply saved preferences to URL once when URL is still default (no explicit filters)
  useEffect(() => {
    if (filtersInitialized) return;
    if (user?.id && !isFilterPrefsFetched) return;

    if (!isDefaultTimeEntryListUrl(searchParams)) {
      setFiltersInitialized(true);
      return;
    }

    if (savedPrefsHydratedRef.current) {
      setFiltersInitialized(true);
      return;
    }
    savedPrefsHydratedRef.current = true;

    const updates = buildTimeEntryListUrlUpdatesFromSaved(savedFilters);
    if (Object.keys(updates).length > 0) {
      patchSearchParams(updates);
    }
    setFiltersInitialized(true);
  }, [
    filtersInitialized,
    isFilterPrefsFetched,
    user?.id,
    searchParams,
    savedFilters,
    patchSearchParams,
  ]);

  // Check if current filters differ from saved
  const savedType = savedFilters.type || [];
  const savedStatus = savedFilters.status || [];
  const savedEngagement = savedFilters.engagement ? (typeof savedFilters.engagement === 'string' ? savedFilters.engagement.split(',') : savedFilters.engagement as string[]) : [];
  const savedUser = savedFilters.user ? (typeof savedFilters.user === 'string' ? savedFilters.user.split(',') : savedFilters.user as string[]) : [];
  const savedMonth = savedFilters.month ? (typeof savedFilters.month === 'string' ? savedFilters.month.split(',') : savedFilters.month as string[]) : [getCurrentMonth()];

  const hasUnsavedFilterChanges = filtersInitialized && (
    JSON.stringify([...filterType].sort()) !== JSON.stringify([...savedType].sort()) ||
    JSON.stringify([...filterStatus].sort()) !== JSON.stringify([...savedStatus].sort()) ||
    JSON.stringify([...filterMonth].sort()) !== JSON.stringify([...savedMonth].sort()) ||
    filterCustomer !== (savedFilters.customer || '') ||
    JSON.stringify([...filterEngagement].sort()) !== JSON.stringify([...savedEngagement].sort()) ||
    JSON.stringify([...filterUser].sort()) !== JSON.stringify([...savedUser].sort())
  );

  const handleSaveFilters = async () => {
    await saveFilters({
      type: filterType.length > 0 ? filterType : undefined,
      status: filterStatus.length > 0 ? filterStatus : undefined,
      month: filterMonth.length > 0 ? filterMonth.join(',') : undefined,
      customer: filterCustomer || undefined,
      engagement: filterEngagement.length > 0 ? filterEngagement.join(',') : undefined,
      user: filterUser.length > 0 ? filterUser.join(',') : undefined,
    });
    setToast({ message: 'Filter preferences saved', type: 'success' });
  };

  // List layout settings
  const {
    columns: layoutColumns,
    visibleColumns,
    saveColumns,
    resetToDefault,
    isSaving: isLayoutSaving,
    isResetting: isLayoutResetting,
  } = useListLayoutPreferences('TIME_ENTRY');

  // Fetch users for dropdown (Admin/Manager only)
  const { data: usersData } = useQuery({
    queryKey: ['timeEntryUsers'],
    queryFn: async () => {
      const response = await api.get('/time-entries/users');
      return response.data as UserOption[];
    },
    enabled: isAdminOrManager && canAccessTimeEntries
  });

  // Fetch groups for dropdown (billing groups)
  const { data: groupsData } = useQuery({
    queryKey: ['billingGroups'],
    queryFn: async () => {
      const response = await api.get('/groups/billing-groups');
      return response.data as { id: string; name: string }[];
    },
    enabled: canAccessTimeEntries
  });

  // Fetch customers for dropdown
  const { data: customersData } = useQuery({
    queryKey: ['customers-dropdown'],
    queryFn: async () => {
      const response = await api.get('/customers/dropdown');
      return response.data;
    },
    enabled: canAccessTimeEntries,
  });

  // Fetch engagements for dropdown (filtered by customer)
  const { data: engagementsData } = useQuery({
    queryKey: ['engagements-dropdown', filterCustomer],
    queryFn: async () => {
      if (!filterCustomer) {
        return { engagements: [] };
      }
      const response = await api.get(`/engagements/dropdown?customerId=${filterCustomer}`);
      return response.data;
    },
    enabled: !!filterCustomer && canAccessTimeEntries,
  });

  // Fetch engagements for export modal (filtered by export customer)
  const { data: exportEngagementsData } = useQuery({
    queryKey: ['exportEngagements', exportCustomerId],
    queryFn: async () => {
      if (!exportCustomerId) {
        return { engagements: [] };
      }
      const response = await api.get(`/engagements?customerId=${exportCustomerId}`);
      return response.data;
    },
    enabled: !!exportCustomerId && showExportModal
  });

  // Column filter options
  const userFilterOptions = useMemo(
    () =>
      usersData?.map((u) => ({
        value: u.id,
        label: `${u.firstName} ${u.lastName}`,
      })) ?? [],
    [usersData]
  );

  const groupFilterOptions = useMemo(
    () =>
      groupsData?.map((g) => ({
        value: g.id,
        label: g.name,
      })) ?? [],
    [groupsData]
  );

  const customerFilterOptions = useMemo(
    () =>
      customersData?.customers?.map((c: { id: string; accountName: string }) => ({
        value: c.id,
        label: c.accountName,
      })) ?? [],
    [customersData]
  );

  const engagementFilterOptions = useMemo(
    () =>
      engagementsData?.engagements?.map((e: { id: string; name: string }) => ({
        value: e.id,
        label: e.name,
      })) ?? [],
    [engagementsData]
  );

  const typeFilterOptions = useMemo(
    () =>
      Object.entries(TYPE_LABELS).map(([value, label]) => ({
        value,
        label,
      })),
    []
  );

  const hoursFilterOptions = useMemo(
    () =>
      timeEnteredPresetOptions.map((opt) => ({
        value: opt.value,
        label: opt.label,
      })),
    []
  );

  const filterSections: FilterSection[] = useMemo(() => {
    const sections: FilterSection[] = [];

    if (isAdminOrManager) {
      sections.push({
        id: 'user',
        label: 'User',
        type: 'searchable-multi-select',
        options: userFilterOptions,
        placeholder: 'All Users',
      });
    }

    sections.push(
      {
        id: 'group',
        label: 'Group',
        type: 'searchable-multi-select',
        options: groupFilterOptions,
        placeholder: 'All Groups',
      },
      {
        id: 'month',
        label: 'Month',
        type: 'multi-select',
        options: monthOptions.map((m) => ({ value: m.value, label: m.label })),
        placeholder: 'All Months',
      },
      {
        id: 'customer',
        label: 'Customer',
        type: 'searchable-select',
        options: customerFilterOptions,
        placeholder: 'All Customers',
      },
      {
        id: 'engagement',
        label: 'Engagement',
        type: 'searchable-multi-select',
        options: engagementFilterOptions,
        placeholder: 'All Engagements',
        disabled: !filterCustomer,
        disabledMessage: 'Select a customer first',
      },
      {
        id: 'type',
        label: 'Activity Type',
        type: 'searchable-multi-select',
        options: typeFilterOptions,
        placeholder: 'All Types',
      },
      {
        id: 'status',
        label: 'Status',
        type: 'multi-select',
        options: statusOptions,
        placeholder: 'All Statuses',
      },
      {
        id: 'timeEntered',
        label: 'Time Entered',
        type: 'duration-range',
        options: hoursFilterOptions,
        placeholder: 'Any Duration',
      }
    );

    return sections;
  }, [
    customerFilterOptions,
    engagementFilterOptions,
    filterCustomer,
    groupFilterOptions,
    hoursFilterOptions,
    isAdminOrManager,
    monthOptions,
    typeFilterOptions,
    userFilterOptions,
  ]);

  const renderColumnHeader = useCallback(
    (columnKey: string, columnLabel: string) => {
      switch (columnKey) {
        case 'number':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterNumber}
              placeholder="Filter by entry #..."
              onChange={(v) => {
                patchSearchParams({ number: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'workedDate':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="multi-select"
              options={monthOptions.map((m) => ({ value: m.value, label: m.label }))}
              value={filterMonth}
              placeholder="All Months"
              onChange={(v) => {
                const months = Array.isArray(v) ? v : [];
                patchSearchParams({
                  month: months.length === 0 ? 'all' : months.join(','),
                  page: '1',
                });
              }}
            />
          );
        case 'user':
          if (!isAdminOrManager) return columnLabel;
          return (
            <TableColumnFilter
              label={columnLabel}
              type="multi-select"
              options={userFilterOptions}
              value={filterUser}
              placeholder="All Users"
              onChange={(v) => {
                const arr = Array.isArray(v) ? v : [];
                patchSearchParams({ user: arr.length ? arr.join(',') : null, page: '1' });
              }}
            />
          );
        case 'customer':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="searchable-select"
              options={customerFilterOptions}
              value={filterCustomer}
              placeholder="All Customers"
              onChange={(v) => {
                patchSearchParams({
                  customer: typeof v === 'string' && v ? v : null,
                  engagement: null,
                  page: '1',
                });
              }}
            />
          );
        case 'engagement':
          if (!filterCustomer) return columnLabel;
          return (
            <TableColumnFilter
              label={columnLabel}
              type="multi-select"
              options={engagementFilterOptions}
              value={filterEngagement}
              placeholder="All Engagements"
              onChange={(v) => {
                const arr = Array.isArray(v) ? v : [];
                patchSearchParams({ engagement: arr.length ? arr.join(',') : null, page: '1' });
              }}
            />
          );
        case 'group':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="multi-select"
              options={groupFilterOptions}
              value={filterGroup}
              placeholder="All Groups"
              onChange={(v) => {
                const arr = Array.isArray(v) ? v : [];
                patchSearchParams({ group: arr.length ? arr.join(',') : null, page: '1' });
              }}
            />
          );
        case 'type':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="multi-select"
              options={typeFilterOptions}
              value={filterType}
              placeholder="All Types"
              onChange={(v) => {
                const arr = Array.isArray(v) ? v : [];
                patchSearchParams({ type: arr.length ? arr.join(',') : null, page: '1' });
              }}
            />
          );
        case 'task':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterTask}
              placeholder="Filter by task..."
              onChange={(v) => {
                patchSearchParams({ task: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'hours':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="single-select"
              options={hoursFilterOptions}
              value={filterTimePreset}
              placeholder="Any Duration"
              onChange={(v) => {
                patchSearchParams({ time: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'description':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterDesc}
              placeholder="Filter by description..."
              onChange={(v) => {
                patchSearchParams({ desc: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'status':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="multi-select"
              options={statusOptions}
              value={filterStatus}
              placeholder="All Statuses"
              onChange={(v) => {
                const arr = Array.isArray(v) ? v : [];
                patchSearchParams({ status: arr.length ? arr.join(',') : null, page: '1' });
              }}
            />
          );
        default:
          return columnLabel;
      }
    },
    [
      filterNumber,
      filterMonth,
      filterUser,
      filterCustomer,
      filterEngagement,
      filterGroup,
      filterType,
      filterTask,
      filterTimePreset,
      filterDesc,
      filterStatus,
      userFilterOptions,
      groupFilterOptions,
      customerFilterOptions,
      engagementFilterOptions,
      typeFilterOptions,
      hoursFilterOptions,
      monthOptions,
      isAdminOrManager,
      patchSearchParams,
    ]
  );

  // Build active filter chips
  const activeFilterChips: FilterChip[] = useMemo(() => {
    const chips: FilterChip[] = [];

    if (appliedSearchTerm) {
      chips.push({
        id: 'search',
        label: 'Search',
        value: `"${appliedSearchTerm}"`,
        color: 'blue',
      });
    }

    if (filterNumber) {
      chips.push({ id: 'number', label: 'Entry #', value: filterNumber, color: 'blue' });
    }

    if (filterDesc) {
      chips.push({ id: 'desc', label: 'Description', value: filterDesc, color: 'blue' });
    }

    if (filterTask) {
      chips.push({ id: 'task', label: 'Task', value: filterTask, color: 'blue' });
    }

    if (filterUser.length > 0 && isAdminOrManager) {
      const userNames = filterUser.map(userId => {
        const selectedUser = usersData?.find(u => u.id === userId);
        return selectedUser ? `${selectedUser.firstName} ${selectedUser.lastName}` : 'Unknown';
      });
      chips.push({
        id: 'user',
        label: 'User',
        value: userNames.length > 2 ? `${userNames.length} selected` : userNames.join(', '),
        color: 'green',
      });
    }

    if (filterGroup.length > 0) {
      const groupNames = filterGroup.map(groupId => {
        const selectedGroup = groupsData?.find(g => g.id === groupId);
        return selectedGroup?.name || 'Unknown';
      });
      chips.push({
        id: 'group',
        label: 'Group',
        value: groupNames.length > 2 ? `${groupNames.length} selected` : groupNames.join(', '),
        color: 'blue',
      });
    }

    if (filterMonth.length > 0) {
      const monthLabels = filterMonth.map(m => {
        const monthOption = monthOptions.find(opt => opt.value === m);
        return monthOption?.label || m;
      });
      chips.push({
        id: 'month',
        label: 'Month',
        value: monthLabels.length > 2 ? `${monthLabels.length} selected` : monthLabels.join(', '),
        color: 'purple',
      });
    }

    if (filterCustomer) {
      const customer = customersData?.customers?.find((c: { id: string }) => c.id === filterCustomer);
      chips.push({
        id: 'customer',
        label: 'Customer',
        value: customer?.accountName || 'Unknown',
        color: 'teal',
      });
    }

    if (filterEngagement.length > 0) {
      const engagementNames = filterEngagement.map(engId => {
        const engagement = engagementsData?.engagements?.find((e: any) => e.id === engId);
        return engagement?.name || 'Unknown';
      });
      chips.push({
        id: 'engagement',
        label: 'Engagement',
        value: engagementNames.length > 2 ? `${engagementNames.length} selected` : engagementNames.join(', '),
        color: 'cyan',
      });
    }

    if (filterType.length > 0) {
      const typeLabels = filterType.map((t) => getTypeLabel(t));
      chips.push({
        id: 'type',
        label: 'Type',
        value: typeLabels.length > 2 ? `${typeLabels.length} selected` : typeLabels.join(', '),
        color: 'orange',
      });
    }

    if (filterStatus.length > 0) {
      chips.push({
        id: 'status',
        label: 'Status',
        value: filterStatus.map(s => statusOptions.find(o => o.value === s)?.label || s).join(', '),
        color: 'red',
      });
    }

    // Time Entered filter chip
    if (filterTimePreset) {
      let chipValue = '';
      if (filterTimePreset.startsWith('custom:')) {
        const [, range] = filterTimePreset.split('custom:');
        const [min, max] = range.split(',');
        if (min && max) {
          chipValue = `${min} - ${max} mins`;
        } else if (min) {
          chipValue = `> ${min} mins`;
        } else if (max) {
          chipValue = `< ${max} mins`;
        }
      } else {
        const preset = timeEnteredPresetOptions.find(p => p.value === filterTimePreset);
        chipValue = preset?.label || filterTimePreset;
      }
      chips.push({
        id: 'timeEntered',
        label: 'Time Entered',
        value: chipValue,
        color: 'purple',
      });
    }

    return chips;
  }, [appliedSearchTerm, filterNumber, filterDesc, filterTask, filterUser, filterGroup, filterMonth, filterCustomer, filterEngagement, filterType, filterStatus, filterTimePreset, usersData, groupsData, customersData, engagementsData, isAdminOrManager, monthOptions]);

  const handleFilterChange = (sectionId: string, value: string | string[]) => {
    switch (sectionId) {
      case 'user': {
        const v = value as string[];
        patchSearchParams({
          user: v.length ? v.join(',') : null,
          page: '1',
        });
        break;
      }
      case 'group': {
        const v = value as string[];
        patchSearchParams({
          group: v.length ? v.join(',') : null,
          page: '1',
        });
        break;
      }
      case 'month': {
        const months = value as string[];
        patchSearchParams({
          month: months.length === 0 ? 'all' : months.join(','),
          page: '1',
        });
        break;
      }
      case 'customer':
        patchSearchParams({
          customer: (value as string) || null,
          engagement: null,
          page: '1',
        });
        break;
      case 'engagement': {
        const v = value as string[];
        patchSearchParams({
          engagement: v.length ? v.join(',') : null,
          page: '1',
        });
        break;
      }
      case 'type': {
        const v = value as string[];
        patchSearchParams({
          type: v.length ? v.join(',') : null,
          page: '1',
        });
        break;
      }
      case 'status': {
        const v = value as string[];
        patchSearchParams({
          status: v.length ? v.join(',') : null,
          page: '1',
        });
        break;
      }
      case 'timeEntered': {
        const v = typeof value === 'string' ? value : '';
        patchSearchParams({
          time: v || null,
          page: '1',
        });
        break;
      }
    }
  };

  // Handle removing a single filter chip
  const handleRemoveFilterChip = (chipId: string) => {
    switch (chipId) {
      case 'search':
        patchSearchParams({ search: null, page: '1' });
        break;
      case 'number':
        patchSearchParams({ number: null, page: '1' });
        break;
      case 'desc':
        patchSearchParams({ desc: null, page: '1' });
        break;
      case 'task':
        patchSearchParams({ task: null, page: '1' });
        break;
      case 'user':
        patchSearchParams({ user: null, page: '1' });
        break;
      case 'group':
        patchSearchParams({ group: null, page: '1' });
        break;
      case 'month':
        patchSearchParams({ month: 'all', page: '1' });
        break;
      case 'customer':
        patchSearchParams({ customer: null, engagement: null, page: '1' });
        break;
      case 'engagement':
        patchSearchParams({ engagement: null, page: '1' });
        break;
      case 'type':
        patchSearchParams({ type: null, page: '1' });
        break;
      case 'status':
        patchSearchParams({ status: null, page: '1' });
        break;
      case 'timeEntered':
        patchSearchParams({ time: null, page: '1' });
        break;
    }
  };

  // Handle clearing all filters
  const handleClearAllFilters = () => {
    const updates: Record<string, string | null | undefined> = {
      search: null,
      number: null,
      desc: null,
      task: null,
      month: getCurrentMonth(),
      customer: null,
      engagement: null,
      type: null,
      user: null,
      group: null,
      status: null,
      time: null,
      page: '1',
      pageSize: null,
    };
    if (isAdmin) {
      updates.hierarchy = null;
    }
    patchSearchParams(updates);
  };

  // Fetch time entries with filters
  const { data, isLoading } = useQuery({
    queryKey: ['timeEntries', page, pageSize, appliedSearchTerm, filterNumber, filterDesc, filterTask, filterMonth, filterCustomer, filterEngagement, filterType, filterUser, filterGroup, filterStatus, filterTimeMin, filterTimeMax, showMyHierarchy],
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: pageSize.toString(),
        offset: ((page - 1) * pageSize).toString(),
        ...(filterCustomer && { customerId: filterCustomer }),
        ...(filterEngagement.length > 0 && { engagementId: filterEngagement.join(',') }),
        ...(filterType.length > 0 && { type: filterType.join(',') }),
        ...(showMyHierarchy && { myHierarchy: 'true' }),
        ...(filterUser.length > 0 && { userId: filterUser.join(',') }),
        ...(filterGroup.length > 0 && { groupId: filterGroup.join(',') }),
        ...(filterStatus.length > 0 && { status: filterStatus.join(',') }),
        ...(filterTimeMin !== null && { minTimeSpent: filterTimeMin.toString() }),
        ...(filterTimeMax !== null && { maxTimeSpent: filterTimeMax.toString() }),
        ...(appliedSearchTerm && { search: appliedSearchTerm }),
        ...(filterNumber && { number: filterNumber }),
        ...(filterDesc && { desc: filterDesc }),
        ...(filterTask && { task: filterTask }),
      });

      // Add date range for month filter (compute range spanning all selected months)
      // Work directly with string format to avoid timezone conversion issues
      if (filterMonth.length > 0) {
        const dateRanges = filterMonth.map(m => {
          const [year, month] = m.split('-');
          const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
          return {
            start: `${year}-${month}-01`,
            end: `${year}-${month}-${String(lastDay).padStart(2, '0')}`
          };
        });
        const sortedStarts = dateRanges.map(d => d.start).sort();
        const sortedEnds = dateRanges.map(d => d.end).sort();
        params.append('startDate', sortedStarts[0]);
        params.append('endDate', sortedEnds[sortedEnds.length - 1]);
        params.append('months', filterMonth.join(','));
      }

      const response = await api.get(`/time-entries?${params}`);
      return response.data;
    },
    enabled: canAccessTimeEntries
  });

  // Fetch stats
  const { data: statsData } = useQuery({
    queryKey: ['timeEntryStats', appliedSearchTerm, filterNumber, filterDesc, filterTask, filterMonth, filterCustomer, filterEngagement, filterType, filterUser, filterGroup, filterStatus, filterTimeMin, filterTimeMax, showMyHierarchy],
    queryFn: async () => {
      const params = new URLSearchParams({
        ...(filterCustomer && { customerId: filterCustomer }),
        ...(filterEngagement.length > 0 && { engagementId: filterEngagement.join(',') }),
        ...(filterType.length > 0 && { type: filterType.join(',') }),
        ...(showMyHierarchy && { myHierarchy: 'true' }),
        ...(filterUser.length > 0 && { userId: filterUser.join(',') }),
        ...(filterGroup.length > 0 && { groupId: filterGroup.join(',') }),
        ...(filterStatus.length > 0 && { status: filterStatus.join(',') }),
        ...(filterTimeMin !== null && { minTimeSpent: filterTimeMin.toString() }),
        ...(filterTimeMax !== null && { maxTimeSpent: filterTimeMax.toString() }),
        ...(appliedSearchTerm && { search: appliedSearchTerm }),
        ...(filterNumber && { number: filterNumber }),
        ...(filterDesc && { desc: filterDesc }),
        ...(filterTask && { task: filterTask }),
      });

      // Add date range for month filter (compute range spanning all selected months)
      // Work directly with string format to avoid timezone conversion issues
      if (filterMonth.length > 0) {
        const dateRanges = filterMonth.map(m => {
          const [year, month] = m.split('-');
          const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
          return {
            start: `${year}-${month}-01`,
            end: `${year}-${month}-${String(lastDay).padStart(2, '0')}`
          };
        });
        const sortedStarts = dateRanges.map(d => d.start).sort();
        const sortedEnds = dateRanges.map(d => d.end).sort();
        params.append('startDate', sortedStarts[0]);
        params.append('endDate', sortedEnds[sortedEnds.length - 1]);
        params.append('months', filterMonth.join(','));
      }

      try {
        const response = await api.get(`/time-entries/stats?${params}`);
        return response.data;
      } catch {
        // Stats endpoint may not exist, return null
        return null;
      }
    },
    enabled: canAccessTimeEntries,
    retry: false
  });

  // Fetch ALL time entries for hierarchy view (page through API until complete).
  // Hierarchy groups by user — a truncated page incorrectly shows "No entries".
  const { data: hierarchyData, isLoading: isLoadingHierarchy } = useQuery({
    queryKey: ['timeEntriesHierarchy', filterNumber, filterDesc, filterTask, filterMonth, filterCustomer, filterEngagement, filterType, filterUser, filterGroup, filterStatus, filterTimeMin, filterTimeMax, appliedSearchTerm],
    queryFn: async () => {
      const buildParams = (limit: number, offset: number) => {
        const params = new URLSearchParams({
          limit: String(limit),
          offset: String(offset),
          myHierarchy: 'true',
          ...(filterCustomer && { customerId: filterCustomer }),
          ...(filterEngagement.length > 0 && { engagementId: filterEngagement.join(',') }),
          ...(filterType.length > 0 && { type: filterType.join(',') }),
          ...(filterUser.length > 0 && { userId: filterUser.join(',') }),
          ...(filterGroup.length > 0 && { groupId: filterGroup.join(',') }),
          ...(filterStatus.length > 0 && { status: filterStatus.join(',') }),
          ...(filterTimeMin !== null && { minTimeSpent: filterTimeMin.toString() }),
          ...(filterTimeMax !== null && { maxTimeSpent: filterTimeMax.toString() }),
          ...(appliedSearchTerm && { search: appliedSearchTerm }),
          ...(filterNumber && { number: filterNumber }),
          ...(filterDesc && { desc: filterDesc }),
          ...(filterTask && { task: filterTask }),
        });

        // Work directly with string format to avoid timezone conversion issues
        if (filterMonth.length > 0) {
          const dateRanges = filterMonth.map(m => {
            const [year, month] = m.split('-');
            const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
            return {
              start: `${year}-${month}-01`,
              end: `${year}-${month}-${String(lastDay).padStart(2, '0')}`
            };
          });
          const sortedStarts = dateRanges.map(d => d.start).sort();
          const sortedEnds = dateRanges.map(d => d.end).sort();
          params.append('startDate', sortedStarts[0]);
          params.append('endDate', sortedEnds[sortedEnds.length - 1]);
          params.append('months', filterMonth.join(','));
        }
        return params;
      };

      // Prefer one large request (backend raises maxLimit for myHierarchy); page if needed.
      const pageSize = 10000;
      const allEntries: TimeEntry[] = [];
      let offset = 0;
      let total = 0;
      let isAdminOrManager = false;

      do {
        const response = await api.get(`/time-entries?${buildParams(pageSize, offset)}`);
        const batch: TimeEntry[] = response.data?.timeEntries || [];
        total = response.data?.total ?? 0;
        isAdminOrManager = Boolean(response.data?.isAdminOrManager);
        allEntries.push(...batch);
        offset += batch.length;
        if (batch.length === 0) break;
      } while (offset < total);

      return { timeEntries: allEntries, total, isAdminOrManager };
    },
    enabled: canAccessTimeEntries && showMyHierarchy // Only fetch when hierarchy view is enabled
  });

  // Approve mutation
  const approveMutation = useMutation({
    mutationFn: async ({ id, remarks }: { id: string; remarks: string }) => {
      const response = await api.post(`/time-entries/${id}/approve`, { remarks });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timeEntries'] });
      queryClient.invalidateQueries({ queryKey: ['timeEntryStats'] });
      queryClient.invalidateQueries({ queryKey: ['timeEntriesHierarchy'] });
      setShowApprovalModal(false);
      setSelectedEntry(null);
      setToast({ message: 'Time entry approved successfully', type: 'success' });
    },
    onError: () => {
      setToast({ message: 'Failed to approve time entry', type: 'error' });
    },
  });

  // Reject mutation
  const rejectMutation = useMutation({
    mutationFn: async ({ id, remarks, actionRequired }: { id: string; remarks: string; actionRequired: string }) => {
      const response = await api.post(`/time-entries/${id}/reject`, { remarks, actionRequired });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timeEntries'] });
      queryClient.invalidateQueries({ queryKey: ['timeEntryStats'] });
      queryClient.invalidateQueries({ queryKey: ['timeEntriesHierarchy'] });
      setShowRejectionModal(false);
      setSelectedEntry(null);
      setToast({ message: 'Time entry rejected successfully', type: 'success' });
    },
    onError: () => {
      setToast({ message: 'Failed to reject time entry', type: 'error' });
    },
  });

  // Bulk approve mutation
  const bulkApproveMutation = useMutation({
    mutationFn: async ({ ids, remarks }: { ids: string[]; remarks: string }) => {
      const response = await api.post('/time-entries/bulk-approve', { ids, remarks });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['timeEntries'] });
      queryClient.invalidateQueries({ queryKey: ['timeEntryStats'] });
      queryClient.invalidateQueries({ queryKey: ['timeEntriesHierarchy'] });
      setShowBulkApprovalModal(false);
      setSelectedEntriesForBulk([]);
      const message = data.failed > 0
        ? `Approved ${data.approved} entries. ${data.failed} failed.`
        : `Successfully approved ${data.approved} entries`;
      setToast({ message, type: data.failed > 0 ? 'warning' : 'success' });
    },
    onError: () => {
      setToast({ message: 'Failed to bulk approve time entries', type: 'error' });
    },
  });

  // Bulk reject mutation
  const bulkRejectMutation = useMutation({
    mutationFn: async ({ ids, remarks, actionRequired }: { ids: string[]; remarks: string; actionRequired: string }) => {
      const response = await api.post('/time-entries/bulk-reject', { ids, remarks, actionRequired });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['timeEntries'] });
      queryClient.invalidateQueries({ queryKey: ['timeEntryStats'] });
      queryClient.invalidateQueries({ queryKey: ['timeEntriesHierarchy'] });
      setShowBulkRejectionModal(false);
      setSelectedEntriesForBulk([]);
      const message = data.failed > 0
        ? `Rejected ${data.rejected} entries. ${data.failed} failed.`
        : `Successfully rejected ${data.rejected} entries`;
      setToast({ message, type: data.failed > 0 ? 'warning' : 'success' });
    },
    onError: () => {
      setToast({ message: 'Failed to bulk reject time entries', type: 'error' });
    },
  });

  // Inline edit: update any editable time-entry field from the list
  const updateTimeEntryMutation = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: Record<string, unknown> }) => {
      const response = await api.put(`/time-entries/${id}`, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timeEntries'] });
      queryClient.invalidateQueries({ queryKey: ['timeEntryStats'] });
      queryClient.invalidateQueries({ queryKey: ['timeEntriesHierarchy'] });
      setToast({ message: 'Time entry updated successfully', type: 'success' });
    },
    onError: (error: any) => {
      setToast({
        message: error?.response?.data?.message || 'Failed to update time entry',
        type: 'error',
      });
    },
  });

  const handleFieldUpdate = useCallback(
    async (entryId: string, payload: Record<string, unknown>) => {
      await updateTimeEntryMutation.mutateAsync({ id: entryId, payload });
    },
    [updateTimeEntryMutation]
  );

  const handleInlineValidationError = useCallback((message: string) => {
    setToast({ message, type: 'error' });
  }, []);

  // Delete time entry (Manager / Admin / Super Admin only)
  const deleteTimeEntryMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete(`/time-entries/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timeEntries'] });
      queryClient.invalidateQueries({ queryKey: ['timeEntryStats'] });
      queryClient.invalidateQueries({ queryKey: ['timeEntriesHierarchy'] });
      setEntryToDelete(null);
      setToast({ message: 'Record Time entry deleted successfully', type: 'success' });
    },
    onError: (error: any) => {
      setToast({
        message: error?.response?.data?.message || 'Failed to delete time entry',
        type: 'error',
      });
    },
  });

  const handleDeleteClick = (entry: TimeEntry) => {
    setEntryToDelete(entry);
  };

  const handleDeleteConfirm = () => {
    if (entryToDelete) {
      deleteTimeEntryMutation.mutate(entryToDelete.id);
    }
  };

  const handleCopyClick = (entry: TimeEntry) => {
    setEntryToCopy(entry);
    setShowCopyModal(true);
  };

  const handleCopySuccess = async (copiedCount: number) => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['timeEntries'] }),
      queryClient.invalidateQueries({ queryKey: ['timeEntriesHierarchy'] }),
      queryClient.invalidateQueries({ queryKey: ['timeEntryStats'] }),
    ]);
    setToast({ message: `Copied ${copiedCount} time entr${copiedCount === 1 ? 'y' : 'ies'} successfully`, type: 'success' });
  };

  // Fetch activity for an entry
  const fetchActivity = async (entryId: string) => {
    setIsLoadingActivities(true);
    try {
      const response = await api.get(`/time-entries/${entryId}/activity`);
      setActivities(response.data.activities || []);
    } catch (error) {
      console.error('Failed to fetch activity:', error);
      setActivities([]);
    } finally {
      setIsLoadingActivities(false);
    }
  };

  // Modal handlers
  const handleApproveClick = (entry: TimeEntry) => {
    setSelectedEntry(entry);
    setShowApprovalModal(true);
  };

  const handleRejectClick = (entry: TimeEntry) => {
    setSelectedEntry(entry);
    setShowRejectionModal(true);
  };

  const handleActivityClick = async (entry: TimeEntry) => {
    setSelectedEntry(entry);
    setShowActivityModal(true);
    await fetchActivity(entry.id);
  };

  const handleApproveConfirm = (remarks: string) => {
    if (selectedEntry) {
      approveMutation.mutate({ id: selectedEntry.id, remarks });
    }
  };

  const handleRejectConfirm = (remarks: string, actionRequired: string) => {
    if (selectedEntry) {
      rejectMutation.mutate({ id: selectedEntry.id, remarks, actionRequired });
    }
  };

  // Bulk action handlers
  const handleBulkApprove = (entries: TimeEntry[]) => {
    setSelectedEntriesForBulk(entries);
    setShowBulkApprovalModal(true);
  };

  const handleBulkReject = (entries: TimeEntry[]) => {
    setSelectedEntriesForBulk(entries);
    setShowBulkRejectionModal(true);
  };

  const handleBulkApproveConfirm = (remarks: string) => {
    if (selectedEntriesForBulk.length > 0) {
      const ids = selectedEntriesForBulk.map(e => e.id);
      bulkApproveMutation.mutate({ ids, remarks });
    }
  };

  const handleBulkRejectConfirm = (remarks: string, actionRequired: string) => {
    if (selectedEntriesForBulk.length > 0) {
      const ids = selectedEntriesForBulk.map(e => e.id);
      bulkRejectMutation.mutate({ ids, remarks, actionRequired });
    }
  };

  const formatDate = formatDateHook;
  const formatWorkedDate = formatDateOnly;

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0 && mins > 0) {
      return `${hours}h ${mins}m`;
    } else if (hours > 0) {
      return `${hours}h`;
    }
    return `${mins}m`;
  };

  const getEntrySummary = (entry: TimeEntry | null) => {
    if (!entry) return undefined;
    return {
      workedDate: formatWorkedDate(entry.workedDate),
      userName: entry.User ? `${entry.User.firstName} ${entry.User.lastName}` : '-',
      type: getTypeLabel(entry.type),
      timeSpent: formatTime(entry.timeSpent),
      customer: entry.Customer?.accountName || '-',
      engagement: entry.Engagement?.name || '-',
    };
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    patchSearchParams({
      search: searchInput.trim() || null,
      page: '1',
    });
  };

  // Handle export customer change
  const handleExportCustomerChange = (customerId: string) => {
    setExportCustomerId(customerId);
    setExportEngagementId('');
    setExportError('');
  };

  // Handle export
  const handleExport = async () => {
    if (!exportMonth) {
      setExportError('Please select a month');
      return;
    }

    setIsExporting(true);
    setExportError('');

    try {
      // Regular users (non-admin/manager/finance) can only export their own entries
      if (!canExportAll) {
        // Use the my-entries endpoint for regular users
        const params = new URLSearchParams({ month: exportMonth });
        const response = await api.get(`/time-entries/export-my-entries?${params.toString()}`);
        const { timeEntries, summary } = response.data;

        if (timeEntries.length === 0) {
          setExportError('No time entries found for the selected month');
          setIsExporting(false);
          return;
        }

        const csvContent = generateMyTimeEntryCSV(timeEntries);
        const userName = (summary.userName || 'user').replace(/[^a-zA-Z0-9]/g, '_');
        const filename = `my-time-entries-${userName}-${exportMonth}.csv`;
        downloadCSV(csvContent, filename);
      } else {
        // Admin/Manager/Finance can use the full export with filters
        const params = new URLSearchParams({ month: exportMonth });
        if (exportCustomerId) {
          params.append('customerId', exportCustomerId);
        }
        if (exportEngagementId) {
          params.append('engagementId', exportEngagementId);
        }

        // Use different endpoint based on export type
        if (includeActivityDetails) {
          // Export with activity/audit details
          if (showMyHierarchy) {
            params.append('myHierarchy', 'true');
          }

          const response = await api.get(`/time-entries/export-with-activity?${params.toString()}`);
          const { timeEntries } = response.data;

          if (timeEntries.length === 0) {
            setExportError('No time entries found for the selected criteria');
            setIsExporting(false);
            return;
          }

          const csvContent = generateTimeEntryActivityCSV(timeEntries);
          const filename = `time-entries-with-activity-${exportMonth}.csv`;
          downloadCSV(csvContent, filename);
        } else {
          // Standard export
          const response = await api.get(`/time-entries/export?${params.toString()}`);
          const { timeEntries, summary } = response.data;

          if (timeEntries.length === 0) {
            setExportError('No time entries found for the selected criteria');
            setIsExporting(false);
            return;
          }

          const csvContent = generateTimeEntryCSV(timeEntries);
          const customerName = summary.customerName.replace(/[^a-zA-Z0-9]/g, '_');
          const engagementName = summary.engagementName.replace(/[^a-zA-Z0-9]/g, '_');
          const filename = `time-entries-${customerName}-${engagementName}-${exportMonth}.csv`;
          downloadCSV(csvContent, filename);
        }
      }

      setShowExportModal(false);
      setExportCustomerId('');
      setExportEngagementId('');
      setExportMonth(monthOptions[0]?.value || '');
      setIncludeActivityDetails(false);
      setToast({ message: 'Export completed successfully', type: 'success' });
    } catch (error: any) {
      console.error('Error exporting time entries:', error);
      setExportError(error?.response?.data?.message || 'Failed to export time entries');
    } finally {
      setIsExporting(false);
    }
  };

  // Time entries from server (search is handled server-side)
  const filteredEntries = data?.timeEntries || [];

  // Calculate stats from data if stats endpoint not available
  const calculatedStats = useMemo(() => {
    if (statsData) return statsData;

    const entries = data?.timeEntries || [];
    const total = data?.total || 0;
    const pending = entries.filter((e: TimeEntry) => e.status === 'PENDING').length;
    const approved = entries.filter((e: TimeEntry) => e.status === 'APPROVED').length;
    const rejected = entries.filter((e: TimeEntry) => e.status === 'REJECTED').length;
    const totalMinutes = entries.reduce((sum: number, e: TimeEntry) => sum + (e.timeSpent || 0), 0);

    return {
      total,
      byStatus: { PENDING: pending, APPROVED: approved, REJECTED: rejected },
      totalHours: totalMinutes / 60,
    };
  }, [statsData, data]);

  const isSavingEntry = (entryId: string) =>
    updateTimeEntryMutation.isPending && updateTimeEntryMutation.variables?.id === entryId;

  const renderInlineField = (
    entry: TimeEntry,
    columnKey: string,
    field: 'workedDate' | 'customer' | 'engagement' | 'group' | 'type' | 'task' | 'hours' | 'description',
    display: React.ReactNode,
    tdClassName = 'px-3 py-2 whitespace-nowrap text-sm text-gray-900'
  ) => (
    <td key={columnKey} className={tdClassName}>
      <TimeEntryInlineEditableCell
        entry={entry}
        field={field}
        canEditTimeEntries={canModifyTimeEntry(entry)}
        isAdminOrManager={isAdminOrManager}
        hasAdminRole={isAdmin}
        display={display}
        todayDate={todayDate}
        onUpdate={handleFieldUpdate}
        isSaving={isSavingEntry(entry.id)}
        onValidationError={handleInlineValidationError}
      />
    </td>
  );

  // Column renderer
  const renderColumn = (entry: TimeEntry, columnKey: string) => {
    switch (columnKey) {
      case 'number':
        return (
          <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm">
            {canModifyTimeEntry(entry) ? (
              <Link
                to={`/time-entries/${entry.id}/edit`}
                state={{ timeEntriesListSearch: location.search }}
                className="text-indigo-600 hover:text-indigo-900 hover:underline font-mono text-xs"
              >
                {entry.number || '-'}
              </Link>
            ) : (
              <span className="font-mono text-xs text-gray-900">{entry.number || '-'}</span>
            )}
          </td>
        );
      case 'workedDate':
        return renderInlineField(entry, columnKey, 'workedDate', formatWorkedDate(entry.workedDate));
      case 'user':
        return (
          <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
            {entry.User ? `${entry.User.firstName} ${entry.User.lastName}` : '-'}
          </td>
        );
      case 'customer':
        return renderInlineField(entry, columnKey, 'customer', entry.Customer?.accountName || '-');
      case 'engagement':
        return renderInlineField(entry, columnKey, 'engagement', entry.Engagement?.name || '-');
      case 'group':
        return renderInlineField(entry, columnKey, 'group', entry.Group?.name || '-');
      case 'type':
        return renderInlineField(
          entry,
          columnKey,
          'type',
          <span title={getTypeLabel(entry.type)} className="truncate max-w-[150px] block">
            {getTypeLabel(entry.type)}
          </span>
        );
      case 'task':
        return renderInlineField(entry, columnKey, 'task', entry.task || '-');
      case 'hours':
        return renderInlineField(
          entry,
          columnKey,
          'hours',
          formatTime(entry.timeSpent),
          'px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900'
        );
      case 'description':
        return renderInlineField(
          entry,
          columnKey,
          'description',
          <div className="max-w-xs truncate" title={stripHtml(entry.description || '')}>
            {stripHtml(entry.description || '') || '-'}
          </div>,
          'px-3 py-2 text-sm text-gray-900'
        );
      case 'status':
        return (
          <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm">
            <ApprovalStatusBadge status={entry.status} />
          </td>
        );
      case 'createdAt':
        return (
          <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
            {formatDate(entry.createdAt)}
          </td>
        );
      default:
        return (
          <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
            -
          </td>
        );
    }
  };

  if (permissionsLoading || isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!canAccessTimeEntries) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">You don't have permission to access time entries.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 flex flex-col h-full min-h-0 overflow-hidden">
      {/* Compact Header Bar */}
      <div className="bg-white shadow rounded-lg mb-2 flex-shrink-0">
        <div className="px-4 py-2">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2">
            {/* Left: Title and Filter */}
            <div className="flex items-center gap-4">
              <h1 className="text-lg font-semibold text-gray-900 whitespace-nowrap">Record Time</h1>
              <FilterDropdown
                sections={filterSections}
                values={{
                  user: filterUser,
                  group: filterGroup,
                  month: filterMonth,
                  customer: filterCustomer,
                  engagement: filterEngagement,
                  type: filterType,
                  status: filterStatus,
                  timeEntered: filterTimePreset,
                }}
                onChange={handleFilterChange}
              />
              {/* Show My Hierarchy Checkbox - optional tree view for Admin/Manager (default: all resources) */}
              {isAdminOrManager && (
                <label className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={showMyHierarchy}
                    onChange={(e) => {
                      patchSearchParams({
                        hierarchy: e.target.checked ? '1' : null,
                        page: '1',
                      });
                    }}
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-gray-700">Show My Hierarchy</span>
                </label>
              )}
            </div>

            {/* Right: Search, New Button, and Settings */}
            <form onSubmit={handleSearch} className="flex items-center gap-2">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search by user or description..."
                  className="pl-8 pr-3 py-1.5 w-64 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>
              <button
                type="submit"
                className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
              >
                Search
              </button>
              {canCreateTimeEntries && (
                <>
                  <button
                    type="button"
                    onClick={() => setShowImportModal(true)}
                    className="inline-flex items-center px-3 py-1.5 text-sm border border-gray-300 rounded-md shadow-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <ArrowUpTrayIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
                    Import
                  </button>
                  <Link
                    to="/time-entries/new"
                    state={{ timeEntriesListSearch: location.search }}
                    className="inline-flex items-center px-3 py-1.5 text-sm border border-transparent rounded-md shadow-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                  >
                    <PlusIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
                    New
                  </Link>
                </>
              )}
              <ListLayoutSettingsButton onClick={() => setShowLayoutSettings(true)} />
            </form>
          </div>

          {/* Active Filters - Inline */}
          {activeFilterChips.length > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-100">
              <ActiveFilterChips
                chips={activeFilterChips}
                onRemove={handleRemoveFilterChip}
                onClearAll={handleClearAllFilters}
                onSaveAsDefault={handleSaveFilters}
                isSaving={isFilterSaving}
                hasUnsavedChanges={hasUnsavedFilterChanges}
              />
            </div>
          )}

          {/* Stats Summary Bar */}
          <div className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-between text-sm">
            <div className="flex items-center gap-6">
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                <span className="text-gray-600">Pending:</span>
                <span className="font-semibold text-gray-900">{calculatedStats?.byStatus?.PENDING ?? '-'}</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-green-500"></span>
                <span className="text-gray-600">Approved:</span>
                <span className="font-semibold text-gray-900">{calculatedStats?.byStatus?.APPROVED ?? '-'}</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-red-500"></span>
                <span className="text-gray-600">Rejected:</span>
                <span className="font-semibold text-gray-900">{calculatedStats?.byStatus?.REJECTED ?? '-'}</span>
              </span>
              <span className="flex items-center gap-1.5">
                <ClockIcon className="h-4 w-4 text-gray-400" />
                <span className="text-gray-600">Total Hours:</span>
                <span className="font-semibold text-gray-900">{calculatedStats?.totalHours?.toFixed(1) ?? '-'}</span>
              </span>
            </div>
            {canExportMyEntries && (
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowExportModal(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  {canExportAll ? 'Export' : 'Export My Entries'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Time Entries Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden flex-1 min-h-0 flex flex-col">
        {showMyHierarchy ? (
          /* Hierarchical Tree View */
          isLoadingHierarchy ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              <span className="ml-3 text-gray-500">Loading hierarchy data...</span>
            </div>
          ) : (
            <HierarchicalTimeEntryTable
              timeEntries={hierarchyData?.timeEntries || []}
              filterMonth={filterMonth}
              filterCustomer={filterCustomer}
              filterEngagement={filterEngagement}
              filterType={filterType}
              filterStatus={filterStatus}
              filterUser={filterUser}
              filterGroup={filterGroup}
              visibleColumns={visibleColumns}
              typeLabels={{ ...TYPE_LABELS, ...DEPRECATED_TYPE_LABELS }}
              isAdminOrManager={isAdminOrManager}
              canEditTimeEntries={canEditTimeEntries}
              hasAdminRole={hasAnyRole(['admin', 'super_admin'])}
              currentUserId={user?.id}
              formatWorkedDate={formatWorkedDate}
              onApproveClick={handleApproveClick}
              onRejectClick={handleRejectClick}
              onActivityClick={handleActivityClick}
              onBulkApprove={handleBulkApprove}
              onBulkReject={handleBulkReject}
              onFieldUpdate={handleFieldUpdate}
              isSavingEntryId={updateTimeEntryMutation.isPending ? updateTimeEntryMutation.variables?.id ?? null : null}
              onValidationError={handleInlineValidationError}
              todayDate={todayDate}
              canDeleteTimeEntries={canDeleteTimeEntries}
              canCreateTimeEntries={canCreateTimeEntries}
              onCopyClick={handleCopyClick}
              onDeleteClick={handleDeleteClick}
              listSearch={location.search}
            />
          )
        ) : (
          /* Regular Table View */
          <>
            <div className="overflow-auto flex-1" style={{ scrollbarGutter: 'stable' }}>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    {visibleColumns.map((col) => (
                      <th key={col.key} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                        {renderColumnHeader(col.key, col.label)}
                      </th>
                    ))}
                    {(canEditTimeEntries || canCreateTimeEntries || isAdminOrManager) && (
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredEntries.length > 0 ? filteredEntries.map((entry: TimeEntry) => (
                    <tr key={entry.id} className="hover:bg-gray-50">
                      {visibleColumns.map((col) => renderColumn(entry, col.key))}
                      {(canEditTimeEntries || canCreateTimeEntries || isAdminOrManager) && (
                        <td className="px-3 py-2 whitespace-nowrap text-sm">
                          <div className="flex items-center gap-2">
                            {/* Edit button - only for pending entries or admins */}
                            {canModifyTimeEntry(entry) && (entry.status !== 'APPROVED' || isAdmin) && (
                              <Link
                                to={`/time-entries/${entry.id}/edit`}
                                state={{ timeEntriesListSearch: location.search }}
                                className="text-indigo-600 hover:text-indigo-900"
                                title="Edit"
                              >
                                <PencilIcon className="h-5 w-5" />
                              </Link>
                            )}
                            {/* Manager actions - Admins can approve their own entries, regular managers cannot */}
                            {isAdminOrManager && entry.status === 'PENDING' && (isAdmin || entry.userId !== user?.id) && (
                              <>
                                <button
                                  onClick={() => handleApproveClick(entry)}
                                  className="text-green-600 hover:text-green-900"
                                  title="Approve"
                                >
                                  <CheckCircleIcon className="h-5 w-5" />
                                </button>
                                <button
                                  onClick={() => handleRejectClick(entry)}
                                  className="text-red-600 hover:text-red-900"
                                  title="Reject"
                                >
                                  <XCircleIcon className="h-5 w-5" />
                                </button>
                              </>
                            )}
                            {canCopyTimeEntry(entry) && (
                              <button
                                type="button"
                                onClick={() => handleCopyClick(entry)}
                                className="text-blue-600 hover:text-blue-900"
                                title="Copy"
                              >
                                <DocumentDuplicateIcon className="h-5 w-5" />
                              </button>
                            )}
                            {/* Activity button - for managers */}
                            {isAdminOrManager && (
                              <button
                                onClick={() => handleActivityClick(entry)}
                                className="text-gray-600 hover:text-gray-900"
                                title="View Activity"
                              >
                                <ClipboardDocumentListIcon className="h-5 w-5" />
                              </button>
                            )}
                            {/* Delete button - own entries for manager; any for admin */}
                            {canDeleteTimeEntry(entry) && (
                              <button
                                onClick={() => handleDeleteClick(entry)}
                                className="text-red-600 hover:text-red-900"
                                title="Delete"
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  )) : null}
                </tbody>
              </table>
            </div>

            {filteredEntries.length === 0 && (
              <div className="text-center py-12">
                <ClockIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No time entries found.</p>
                {canCreateTimeEntries && (
                  <Link
                    to="/time-entries/new"
                    state={{ timeEntriesListSearch: location.search }}
                    className="inline-flex items-center mt-4 text-blue-600 hover:text-blue-700"
                  >
                    <PlusIcon className="h-5 w-5 mr-1" />
                    Record your first time entry
                  </Link>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Pagination Controls — flat list only; hierarchy loads the full filtered set */}
      <div className="mt-2 flex flex-col sm:flex-row items-center justify-between gap-2 bg-white px-3 py-2 rounded-lg shadow flex-shrink-0" style={{ minHeight: '48px' }}>
        {showMyHierarchy ? (
          <div className="text-sm text-gray-700 w-full text-center">
            {(() => {
              const hierarchyTotal = hierarchyData?.total ?? 0;
              return hierarchyTotal > 0
                ? `${hierarchyTotal} ${hierarchyTotal === 1 ? 'entry' : 'entries'} in hierarchy view`
                : 'No records';
            })()}
          </div>
        ) : data?.total > 0 ? (
          <>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-700">Show</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  patchSearchParams({
                    pageSize: e.target.value,
                    page: '1',
                  });
                }}
                className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
              <span className="text-sm text-gray-700">entries</span>
            </div>

            <div className="text-sm text-gray-700">
              Showing {Math.min((page - 1) * pageSize + 1, data.total)} to {Math.min(page * pageSize, data.total)} of {data.total} entries
            </div>

            <div className="flex items-center gap-1">
              <button
                onClick={() => patchSearchParams({ page: '1' })}
                disabled={page === 1}
                className="px-2 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                &laquo;
              </button>
              <button
                onClick={() => patchSearchParams({ page: String(page - 1) })}
                disabled={page === 1}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>

              {(() => {
                const totalPages = Math.ceil(data.total / pageSize);
                const pages: (number | string)[] = [];

                if (totalPages <= 7) {
                  for (let i = 1; i <= totalPages; i++) pages.push(i);
                } else {
                  if (page <= 3) {
                    pages.push(1, 2, 3, 4, '...', totalPages);
                  } else if (page >= totalPages - 2) {
                    pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
                  } else {
                    pages.push(1, '...', page - 1, page, page + 1, '...', totalPages);
                  }
                }

                return pages.map((p, idx) => (
                  p === '...' ? (
                    <span key={`ellipsis-${idx}`} className="px-2 py-1 text-sm text-gray-500">...</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => patchSearchParams({ page: String(p) })}
                      className={`px-3 py-1 text-sm border rounded-md ${
                        page === p
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {p}
                    </button>
                  )
                ));
              })()}

              <button
                onClick={() => patchSearchParams({ page: String(page + 1) })}
                disabled={page >= Math.ceil(data.total / pageSize)}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
              <button
                onClick={() =>
                  patchSearchParams({
                    page: String(Math.ceil(data.total / pageSize)),
                  })
                }
                disabled={page >= Math.ceil(data.total / pageSize)}
                className="px-2 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                &raquo;
              </button>
            </div>
          </>
        ) : (
          <div className="text-sm text-gray-500 w-full text-center">No records</div>
        )}
      </div>

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => {
              setShowExportModal(false);
              setExportError('');
              setIncludeActivityDetails(false);
            }} />
            <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <div className="absolute right-0 top-0 pr-4 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowExportModal(false);
                    setExportError('');
                    setIncludeActivityDetails(false);
                  }}
                  className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100 sm:mx-0 sm:h-10 sm:w-10">
                  <ArrowDownTrayIcon className="h-6 w-6 text-indigo-600" />
                </div>
                <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                  <h3 className="text-lg font-semibold leading-6 text-gray-900">
                    {canExportAll ? 'Export Time Entries' : 'Export My Time Entries'}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {canExportAll
                      ? 'Select month to export time entries. Optionally filter by customer and engagement.'
                      : 'Select month to export your time entries to CSV.'}
                  </p>
                  <div className="mt-4 space-y-4">
                    {/* Customer/Engagement filters - only for admin/manager/finance */}
                    {canExportAll && (
                      <>
                        <div>
                          <label htmlFor="export-customer" className="block text-sm font-medium text-gray-700">
                            Customer
                          </label>
                          <select
                            id="export-customer"
                            value={exportCustomerId}
                            onChange={(e) => handleExportCustomerChange(e.target.value)}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          >
                            <option value="">All Customers</option>
                            {customersData?.customers?.map((customer: any) => (
                              <option key={customer.id} value={customer.id}>
                                {customer.accountName || customer.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        {exportCustomerId && (
                          <div>
                            <label htmlFor="export-engagement" className="block text-sm font-medium text-gray-700">
                              Engagement
                            </label>
                            <select
                              id="export-engagement"
                              value={exportEngagementId}
                              onChange={(e) => {
                                setExportEngagementId(e.target.value);
                                setExportError('');
                              }}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            >
                              <option value="">All Engagements</option>
                              {exportEngagementsData?.engagements?.map((engagement: any) => (
                                <option key={engagement.id} value={engagement.id}>
                                  {engagement.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      </>
                    )}

                    <div>
                      <label htmlFor="export-month" className="block text-sm font-medium text-gray-700">
                        Month <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="export-month"
                        value={exportMonth}
                        onChange={(e) => {
                          setExportMonth(e.target.value);
                          setExportError('');
                        }}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      >
                        {monthOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Include Activity Details Checkbox - only for admin/manager/finance */}
                    {canExportAll && (
                      <div className="flex items-start">
                        <div className="flex h-6 items-center">
                          <input
                            id="include-activity"
                            name="include-activity"
                            type="checkbox"
                            checked={includeActivityDetails}
                            onChange={(e) => setIncludeActivityDetails(e.target.checked)}
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                          />
                        </div>
                        <div className="ml-3 text-sm leading-6">
                          <label htmlFor="include-activity" className="font-medium text-gray-900">
                            Include Approval Audit Details
                          </label>
                          <p className="text-gray-500">
                            Export with RM name, approval status, actions, remarks, and timestamps
                          </p>
                        </div>
                      </div>
                    )}

                    {exportError && (
                      <div className="rounded-md bg-red-50 p-3">
                        <p className="text-sm text-red-700">{exportError}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleExport}
                  disabled={isExporting || !exportMonth}
                  className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 sm:ml-3 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isExporting ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                      Exporting...
                    </>
                  ) : (
                    <>
                      <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                      Export CSV
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowExportModal(false);
                    setExportError('');
                    setIncludeActivityDetails(false);
                  }}
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <TimeEntryApprovalModal
        isOpen={showApprovalModal}
        onClose={() => {
          setShowApprovalModal(false);
          setSelectedEntry(null);
        }}
        onConfirm={handleApproveConfirm}
        isLoading={approveMutation.isPending}
        entrySummary={getEntrySummary(selectedEntry)}
      />

      <TimeEntryRejectionModal
        isOpen={showRejectionModal}
        onClose={() => {
          setShowRejectionModal(false);
          setSelectedEntry(null);
        }}
        onConfirm={handleRejectConfirm}
        isLoading={rejectMutation.isPending}
        entrySummary={getEntrySummary(selectedEntry)}
      />

      <TimeEntryActivityModal
        isOpen={showActivityModal}
        onClose={() => {
          setShowActivityModal(false);
          setSelectedEntry(null);
          setActivities([]);
        }}
        activities={activities}
        isLoading={isLoadingActivities}
        entrySummary={selectedEntry ? {
          workedDate: formatWorkedDate(selectedEntry.workedDate),
          userName: selectedEntry.User ? `${selectedEntry.User.firstName} ${selectedEntry.User.lastName}` : '-',
        } : undefined}
      />

      <CopyTimeEntryModal
        isOpen={showCopyModal}
        onClose={() => {
          setShowCopyModal(false);
          setEntryToCopy(null);
        }}
        onSuccess={handleCopySuccess}
        entry={entryToCopy}
      />

      <TimeEntryImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onSuccess={async (created) => {
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: ['timeEntries'] }),
            queryClient.invalidateQueries({ queryKey: ['timeEntriesHierarchy'] }),
            queryClient.invalidateQueries({ queryKey: ['timeEntryStats'] }),
          ]);
          setToast({
            message: `Successfully imported ${created} time ${created === 1 ? 'entry' : 'entries'}`,
            type: 'success',
          });
          setShowImportModal(false);
        }}
      />

      {/* Delete Record Time confirmation modal */}
      {entryToDelete && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-gray-500/75 transition-opacity" aria-hidden="true" onClick={() => !deleteTimeEntryMutation.isPending && setEntryToDelete(null)} />
            <div className="relative bg-white rounded-lg shadow-xl max-w-xl w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Record Time Entry</h3>
              <p className="text-sm text-gray-600 mb-3">Are you sure you want to delete this Record Time entry?</p>
              <div className="text-sm text-gray-700 mb-6 pl-1 border-l-2 border-gray-200 grid grid-cols-[auto_1fr] gap-x-6 gap-y-1.5">
                <p><span className="font-medium text-gray-500">User:</span> {entryToDelete.User ? `${entryToDelete.User.firstName} ${entryToDelete.User.lastName}` : '-'}</p>
                <p><span className="font-medium text-gray-500">Customer:</span> {entryToDelete.Customer?.accountName || '-'}</p>
                <p><span className="font-medium text-gray-500">Date:</span> {formatWorkedDate(entryToDelete.workedDate)}</p>
                <p><span className="font-medium text-gray-500">Engagement:</span> {entryToDelete.Engagement?.name || '-'}</p>
                <p><span className="font-medium text-gray-500">Time:</span> {formatTime(entryToDelete.timeSpent)}</p>
                <p><span className="font-medium text-gray-500">Group:</span> {entryToDelete.Group?.name || '-'}</p>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => !deleteTimeEntryMutation.isPending && setEntryToDelete(null)}
                  disabled={deleteTimeEntryMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteConfirm}
                  disabled={deleteTimeEntryMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {deleteTimeEntryMutation.isPending ? (
                    <>
                      <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    'Delete'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ListLayoutSettingsModal
        isOpen={showLayoutSettings}
        onClose={() => setShowLayoutSettings(false)}
        entityType="TIME_ENTRY"
        columns={layoutColumns}
        onSave={saveColumns}
        onReset={resetToDefault}
        isSaving={isLayoutSaving}
        isResetting={isLayoutResetting}
      />

      {/* Bulk Approval Modal */}
      <TimeEntryApprovalModal
        isOpen={showBulkApprovalModal}
        onClose={() => {
          setShowBulkApprovalModal(false);
          setSelectedEntriesForBulk([]);
        }}
        onConfirm={handleBulkApproveConfirm}
        isLoading={bulkApproveMutation.isPending}
        entrySummary={selectedEntriesForBulk.length > 0 ? {
          workedDate: `${selectedEntriesForBulk.length} entries selected`,
          userName: `${new Set(selectedEntriesForBulk.map(e => e.userId)).size} users`,
          type: 'Bulk Approval',
          timeSpent: formatTime(selectedEntriesForBulk.reduce((sum, e) => sum + e.timeSpent, 0)),
          customer: '-',
          engagement: '-',
        } : undefined}
        title={`Approve ${selectedEntriesForBulk.length} Time Entries`}
      />

      {/* Bulk Rejection Modal */}
      <TimeEntryRejectionModal
        isOpen={showBulkRejectionModal}
        onClose={() => {
          setShowBulkRejectionModal(false);
          setSelectedEntriesForBulk([]);
        }}
        onConfirm={handleBulkRejectConfirm}
        isLoading={bulkRejectMutation.isPending}
        entrySummary={selectedEntriesForBulk.length > 0 ? {
          workedDate: `${selectedEntriesForBulk.length} entries selected`,
          userName: `${new Set(selectedEntriesForBulk.map(e => e.userId)).size} users`,
          type: 'Bulk Rejection',
          timeSpent: formatTime(selectedEntriesForBulk.reduce((sum, e) => sum + e.timeSpent, 0)),
          customer: '-',
          engagement: '-',
        } : undefined}
        title={`Reject ${selectedEntriesForBulk.length} Time Entries`}
      />

      {/* Toast Notification */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 animate-slide-in-right">
          <div
            className={`rounded-lg shadow-lg px-3 py-2 max-w-md ${
              toast.type === 'success'
                ? 'bg-green-50 border border-green-200'
                : toast.type === 'error'
                ? 'bg-red-50 border border-red-200'
                : 'bg-yellow-50 border border-yellow-200'
            }`}
          >
            <div className="flex items-start gap-3">
              {toast.type === 'success' && (
                <CheckCircleIcon className="h-6 w-6 text-green-600 flex-shrink-0" />
              )}
              {toast.type === 'error' && (
                <XMarkIcon className="h-6 w-6 text-red-600 flex-shrink-0" />
              )}
              {toast.type === 'warning' && (
                <ClockIcon className="h-6 w-6 text-yellow-600 flex-shrink-0" />
              )}
              <div className="flex-1">
                <p
                  className={`text-sm font-medium ${
                    toast.type === 'success'
                      ? 'text-green-800'
                      : toast.type === 'error'
                      ? 'text-red-800'
                      : 'text-yellow-800'
                  }`}
                >
                  {toast.message}
                </p>
              </div>
              <button
                onClick={() => setToast(null)}
                className={`flex-shrink-0 ${
                  toast.type === 'success'
                    ? 'text-green-400 hover:text-green-600'
                    : toast.type === 'error'
                    ? 'text-red-400 hover:text-red-600'
                    : 'text-yellow-400 hover:text-yellow-600'
                }`}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
